import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { getToken } from '../../services/authToken';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getAuthHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ── fetchNotifications ────────────────────────────────────────────────────────
async function fetchNotifications() {
  const res = await axios.get(`${API}/notifications?limit=50`, {
    headers: getAuthHeaders(),
  });
  // Backend: { notifications: [...], unread_count }
  return res.data;
}

/**
 * useNotifications — GET /api/notifications
 *
 * Returns:
 *   data.notifications  : bildirim listesi
 *   data.unread_count   : okunmamış sayı
 *   isLoading, isError, refetch
 *
 * Refetch: 60sn interval + window focus
 * Enabled: getToken() mevcut iken
 */
export function useNotifications() {
  return useQuery({
    queryKey: ['notifications', 'feed'],
    queryFn: fetchNotifications,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
    refetchOnWindowFocus: true,
    enabled: typeof window !== 'undefined' && !!getToken(),
  });
}

// ── useMarkRead — PATCH /api/notifications/{id}/read ──────────────────────────
export function useMarkRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId) => {
      const res = await axios.patch(
        `${API}/notifications/${notificationId}/read`,
        {},
        { headers: getAuthHeaders() }
      );
      return res.data;
    },
    onMutate: async (notificationId) => {
      // Optimistik güncelleme
      await queryClient.cancelQueries({ queryKey: ['notifications', 'feed'] });
      const prev = queryClient.getQueryData(['notifications', 'feed']);

      queryClient.setQueryData(['notifications', 'feed'], (old) => {
        if (!old) return old;
        const updated = old.notifications.map((n) =>
          n.notification_id === notificationId ? { ...n, is_read: true } : n
        );
        const unread_count = updated.filter((n) => !n.is_read).length;
        return { ...old, notifications: updated, unread_count };
      });

      return { prev };
    },
    onError: (_err, _vars, context) => {
      if (context?.prev) {
        queryClient.setQueryData(['notifications', 'feed'], context.prev);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', 'feed'] });
    },
  });
}

// ── useMarkAllRead — POST /api/notifications/mark-all-read ───────────────────
export function useMarkAllRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const res = await axios.post(
        `${API}/notifications/mark-all-read`,
        {},
        { headers: getAuthHeaders() }
      );
      return res.data;
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['notifications', 'feed'] });
      const prev = queryClient.getQueryData(['notifications', 'feed']);

      queryClient.setQueryData(['notifications', 'feed'], (old) => {
        if (!old) return old;
        const updated = old.notifications.map((n) => ({ ...n, is_read: true }));
        return { ...old, notifications: updated, unread_count: 0 };
      });

      return { prev };
    },
    onError: (_err, _vars, context) => {
      if (context?.prev) {
        queryClient.setQueryData(['notifications', 'feed'], context.prev);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', 'feed'] });
    },
  });
}
